import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ParametrosService } from 'app/services/parametros.service';
import { VoucherType } from 'app/models/voucher-type.models';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-voucher-types',
  templateUrl: './voucher-types.component.html',
  styleUrls: ['./voucher-types.component.css'],
})
export class VoucherTypesComponent implements OnInit {
  voucherTypes = [];
  groupedVoucherTypes: any = {};
  isLoading = false;

  // Relación para traducir los tipos de operación
  operationTypes = [
    { value: 'sales', label: 'Ventas' },
    { value: 'purchases', label: 'Compras' },
    { value: 'stock', label: 'Stock' },
    { value: 'treasury', label: 'Tesorería' }
  ];

  constructor(
    private parametrosService: ParametrosService,
    private router: Router,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Tipos de Comprobante';
    }
    this.getVoucherTypes();
  }

  getVoucherTypes() {
    this.isLoading = true;
    this.parametrosService.getVoucherTypes(null, 'true').subscribe(
      data => {
        // Los datos ya vienen agrupados desde el backend
        this.processGroupedVoucherTypes(data);
        this.isLoading = false;
      },
      err => {
        console.error('Error al obtener los tipos de comprobante', err);
        this.openSnackBar('Error al obtener los tipos de comprobante', 'Cerrar');
        this.isLoading = false;
      }
    );
  }

  processGroupedVoucherTypes(groupedData: any[]) {
    this.groupedVoucherTypes = {};
    this.voucherTypes = []; // Para mantener compatibilidad con métodos existentes
    
    groupedData.forEach(group => {
      const operationType = group.operationType;
      
      this.groupedVoucherTypes[operationType] = {
        label: this.getOperationTypeLabel(operationType),
        items: group.voucherTypes,
        count: group.count
      };
      
      // Agregamos todos los voucher types a la lista plana para compatibilidad
      this.voucherTypes = [...this.voucherTypes, ...group.voucherTypes];
    });
  }

  getGroupKeys(): string[] {
    return Object.keys(this.groupedVoucherTypes);
  }

  nuevoVoucherType() {
    this.router.navigate(['/configurar/voucher-type/create']);
  }

  editarVoucherType(voucherType: VoucherType) {
    // Verificar si es un tipo de comprobante por defecto
    if (voucherType.default) {
      this.openSnackBar('No se puede modificar un tipo de comprobante por defecto del sistema', 'Cerrar');
      return;
    }
    
    this.router.navigate(['/configurar/voucher-type/edit', voucherType.id]);
  }

  borrarVoucherType(voucherType: VoucherType) {
    const { id } = voucherType;
    
    // Verificar si es un tipo de comprobante por defecto
    if (voucherType.default) {
      this.openSnackBar('No se puede eliminar un tipo de comprobante por defecto del sistema', 'Cerrar');
      return;
    }
    
    // Verificar si tiene talonarios asociados
    if ((voucherType.receiptBooksCount ?? 0) > 0) {
      this.openSnackBar('No se puede eliminar este tipo de comprobante porque tiene talonarios asociados', 'Cerrar');
      return;
    }
    
    // Confirmación antes de eliminar
    if (!confirm('¿Está seguro de que desea eliminar este tipo de comprobante?')) {
      return;
    }
    
    // Temporalmente eliminar del array local
    this.voucherTypes = this.voucherTypes.filter(v => v.id !== id);
    
    // Reagrupar después de eliminar
    this.regenerateGroupedData();
    this.openSnackBar("Tipo de comprobante eliminado correctamente", "Cerrar");
    return;
  }

  regenerateGroupedData() {
    // Reagrupa los datos después de modificaciones locales
    const tempGrouped = {};
    
    this.voucherTypes.forEach(voucherType => {
      const operationType = voucherType.operationType;
      
      if (!tempGrouped[operationType]) {
        tempGrouped[operationType] = {
          label: this.getOperationTypeLabel(operationType),
          items: [],
          count: 0
        };
      }
      
      tempGrouped[operationType].items.push(voucherType);
      tempGrouped[operationType].count++;
    });
    
    this.groupedVoucherTypes = tempGrouped;
  }

  // Método para generar el tooltip de talonarios asociados
  getReceiptBooksTooltip(voucherType: any): string {
    const count = voucherType?.receiptBooksCount ?? 0;
    
    if (count === 0) {
      return '';
    }
    
    // Si existe información detallada de los talonarios
    if (voucherType?.receiptBooks && Array.isArray(voucherType.receiptBooks)) {
      const names = voucherType.receiptBooks.map(rb => rb.name || rb.description || `Talonario ${rb.id}`);
      return `Talonarios asociados:\n${names.join('\n')}`;
    }
    
    // Si solo tenemos el count, mostrar información genérica
    return `${count} talonario${count > 1 ? 's' : ''} asociado${count > 1 ? 's' : ''}`;
  }

  // Método para generar el tooltip de eliminación
  getDeleteTooltip(voucherType: any): string {
    if (voucherType?.default) {
      return 'No se puede eliminar un tipo de comprobante por defecto del sistema';
    }
    
    const receiptBooksCount = voucherType?.receiptBooksCount ?? 0;
    if (receiptBooksCount > 0) {
      return 'No se puede eliminar porque tiene talonarios asociados';
    }
    
    return 'Eliminar tipo de comprobante';
  }

  openSnackBar(message: string, type) {
    this.snackBar.open(message, type, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  // Método para traducir el tipo de operación
  getOperationTypeLabel(operationType: string): string {
    const operationType_obj = this.operationTypes.find(type => type.value === operationType);
    return operationType_obj ? operationType_obj.label : operationType;
  }
}
